/** Suffixes used by the API's raw readiness errors, e.g. "HoursOpen is required". */
const REQUIRED_SUFFIXES = [" is required", " is required."] as const;

const normalize = (value: string): string =>
	value.toLowerCase().replaceAll(/[^a-z]/g, "");

/** Returns the field name when a line is a raw "<field> is required" error. */
const getRequiredFieldName = (line: string): string | null => {
	const lowerCased = line.toLowerCase();
	for (const suffix of REQUIRED_SUFFIXES) {
		if (lowerCased.endsWith(suffix)) {
			return line.slice(0, line.length - suffix.length);
		}
	}
	return null;
};

export type TenderFailureMessage = {
	/** Message text to show above the missing-detail list. */
	message: string;
	/** Friendly labels for the details the shipment is still missing. */
	missingDetails: string[];
};

/**
 * The bulk-tender readiness errors returned by the API list raw field names
 * ("HoursOpen is required") and omit details the shipment also needs, such as
 * the delivery contact phone. When the client-side validation recognizes at
 * least one of those raw lines, swap them for the complete, friendly list the
 * Details Needed banner uses so nothing required is left off the modal.
 *
 * Any API line that is not covered by the client-side validation is kept as-is,
 * so unrelated failures (eTag conflicts, credit holds, unknown required fields)
 * still surface verbatim.
 */
export const buildTenderFailureMessage = (
	apiMessage: string,
	missingDetails: readonly string[],
): TenderFailureMessage => {
	const unchanged: TenderFailureMessage = {
		message: apiMessage,
		missingDetails: [],
	};
	if (missingDetails.length === 0) return unchanged;

	const normalizedLabels = missingDetails.map((label) => normalize(label));
	const lines = apiMessage
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line.length > 0);

	let hasRecognizedLine = false;
	const remainingLines = lines.filter((line) => {
		const fieldName = getRequiredFieldName(line);
		if (fieldName === null) return true;

		const field = normalize(fieldName);
		const isRecognized =
			field.length > 0 &&
			normalizedLabels.some((label) => label.includes(field));
		if (isRecognized) hasRecognizedLine = true;

		return !isRecognized;
	});

	if (!hasRecognizedLine) return unchanged;

	return {
		message: remainingLines.join("\n"),
		missingDetails: [...missingDetails],
	};
};
